<template>
  <nav class="compact-navbar" :class="{ 'is-mobile': mobile }" @keydown.esc.stop="closeMenu(true)">
    <ul class="nav-list">
      <li v-for="(item, index) in navItems" :key="index" class="nav-item"
          @mouseenter="isProducts(item) && !mobile && openMenu()"
          @mouseleave="!mobile && scheduleClose()" @focusout="onFocusOut">
        <template v-if="item.name === $t('search.language_4')">
          <div v-if="!mobile" class="nav-tools">
            <button type="button" :aria-label="$t('search.language_4')" @click="openWebSearch"><i class="el-icon-search" /></button>
            <button type="button" @click="checkLanguage">{{ Language }}</button>
            <button type="button" :aria-label="$t('personalCenter.favorites')" @click="goToFavorites">
              <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" :fill="favCount ? '#e0245e' : 'none'" :stroke="favCount ? '#e0245e' : '#fff'" stroke-width="1.5" />
              </svg>
              <span v-if="favCount" class="favorite-count">{{ favCount > 99 ? '99+' : favCount }}</span>
            </button>
          </div>
        </template>
        <template v-else-if="isProducts(item)">
          <button ref="productTrigger" type="button" class="nav-link" :class="{ active: isActive(item) || menuOpen }"
                  :aria-expanded="menuOpen" @click="menuOpen ? closeMenu() : openMenu()" @keydown.down.prevent="focusMenu">
            {{ item.name }}
          </button>
          <ProductMenu v-if="menuOpen" ref="productMenu" class="dropdown-panel" :style="mobile ? null : { top: dropdownTop + 'px' }" :categories="drawerList" @mouseenter.native="cancelClose" @navigate="onNavigate" />
        </template>
        <a v-else :href="herfUrl(item)" class="nav-link" :class="{ active: isActive(item) }" @click.prevent="navigatorTo(item)">{{ item.name }}</a>
      </li>
    </ul>
    <el-dialog :visible.sync="languageDialog" width="280px" append-to-body :show-close="false">
      <button v-for="lang in langList" :key="lang.country" type="button" class="language-option" @click="changeLanguage(lang)">{{ lang.text }}</button>
    </el-dialog>
  </nav>
</template>

<script>
import LegacyNavbar from '@/components/navbar'
import ProductMenu from './ProductMenu.vue'

// Reuse existing API, search, favorites and language behavior without changing the original component.
export default {
  name: 'ProductNavbar',
  extends: LegacyNavbar,
  components: { ProductMenu },
  props: { mobile: { type: Boolean, default: false } },
  data: () => ({ menuOpen: false, dropdownTop: 0, closeTimer: null }),
  watch: { '$route.fullPath'() { this.closeMenu() } },
  mounted() {
    document.addEventListener('click', this.onOutsideClick)
    window.addEventListener('resize', this.updateMenuPosition)
  },
  beforeDestroy() {
    this.cancelClose()
    document.removeEventListener('click', this.onOutsideClick)
    window.removeEventListener('resize', this.updateMenuPosition)
  },
  methods: {
    openWebSearch() {
      this.closeMenu()
      this.openSearch()
    },
    isProducts(item) { return /\/products(?:\?|$)/.test(item.url || '') },
    isActive(item) {
      const path = (item.url || '').split('?')[0]
      return path && (this.$route.path === path || (!path.endsWith('/') && this.$route.path.startsWith(`${path}/`)))
    },
    closeMenu(restoreFocus = false) {
      this.cancelClose()
      this.menuOpen = false
      if (restoreFocus && this.$refs.productTrigger) this.$refs.productTrigger[0].focus()
    },
    updateMenuPosition() {
      const header = this.$el.closest('.header-body') || this.$el
      this.dropdownTop = header.getBoundingClientRect().bottom
    },
    openMenu() {
      this.cancelClose()
      this.updateMenuPosition()
      this.menuOpen = true
    },
    cancelClose() { clearTimeout(this.closeTimer); this.closeTimer = null },
    scheduleClose() {
      this.cancelClose()
      this.closeTimer = setTimeout(() => this.closeMenu(), 180)
    },
    onOutsideClick(event) { if (!this.$el.contains(event.target)) this.closeMenu() },
    onFocusOut(event) { if (!event.currentTarget.contains(event.relatedTarget)) this.closeMenu() },
    onNavigate() { this.closeMenu(); this.$emit('closeFun') },
    focusMenu() {
      this.openMenu()
      this.$nextTick(() => this.$refs.productMenu[0].$el.querySelector('a').focus())
    },
    // The new menu owns its open state instead of mutating the supplied navigation items.
    toggleDropUp() { this.closeMenu() }
  }
}
</script>

<style scoped>
.compact-navbar .nav-list { display: flex; align-items: stretch; min-height: 5.625rem; gap: 1rem; padding: 0; margin: 0; list-style: none; }
.compact-navbar .nav-item { position: relative; display: flex; align-items: center; flex-shrink: 0; padding: 0; margin: 0; height: auto; box-sizing: border-box; }
.compact-navbar .nav-link { display: inline-flex; align-items: center; padding: .625rem; margin: 0; border: 0; background: none; color: #fff; font: inherit; font-size: 1.375rem; line-height: 1.5; white-space: nowrap; text-decoration: none; cursor: pointer; }
.nav-link:hover, .nav-link.active { text-decoration: underline; text-underline-offset: 8px; text-decoration-thickness: 1px; }
.nav-link:focus-visible, .nav-tools button:focus-visible { outline: 1px solid #fff; outline-offset: 2px; }
.dropdown-panel { position: fixed; left: 0; right: 0; width: 100%; z-index: 10; max-height: 75vh; overflow-y: auto; box-shadow: 0 12px 24px rgba(0, 0, 0, .18); }
.nav-tools { display: flex; align-items: center; gap: 20px; padding-left: 10px; }
.nav-tools button { position: relative; display: inline-flex; align-items: center; justify-content: center; padding: 0; margin: 0; border: 0; background: none; color: #fff; font: inherit; font-size: 1.375rem; line-height: 1.5; cursor: pointer; }
.nav-tools svg, .nav-tools i { display: block; flex-shrink: 0; line-height: 1; }
.favorite-count { position: absolute; top: -7px; right: -9px; font-size: 10px; }
.language-option { display: block; width: 100%; padding: 12px; border: 0; background: transparent; text-align: left; cursor: pointer; }
.language-option:hover { background: #eee; }
.compact-navbar.is-mobile .nav-list { display: block; min-height: 0; }
.compact-navbar.is-mobile .nav-item { display: block; padding: 0; }
.compact-navbar.is-mobile .nav-link { color: #222; padding: 14px 20px; font-size: 16px; }
.is-mobile .dropdown-panel { position: static; box-shadow: none; }
.compact-navbar:not(.is-mobile) .nav-list { flex-wrap: wrap; justify-content: center; align-content: center; gap: 0 clamp(2px, .5vw, 12px); }
.compact-navbar:not(.is-mobile) .nav-link { padding: 10px clamp(3px, .4vw, 8px); font-size: clamp(14px, 1.05vw, 22px); }
.compact-navbar:not(.is-mobile) .nav-tools { gap: 8px; padding: 0 4px; }
.compact-navbar:not(.is-mobile) .nav-tools button { min-width: 32px; min-height: 40px; }
@media (min-width: 1025px) and (max-width: 1200px) {
  .compact-navbar:not(.is-mobile) .nav-link { font-size: 13px; padding: 10px 3px; }
}
</style>
